/**
 * Single source of truth for mark ↔ markdown syntax.
 * Both the renderer and parser use this table, so adding a new mark type
 * means adding one entry here — impossible to forget one side.
 */

import { Mark } from './astTypes'

export interface MarkFormatEntry {
  /** Does this entry handle the given AST mark? */
  matchMark: (mark: Mark) => boolean
  /** Wrap text with the mark's markdown syntax */
  render: (text: string, mark: Mark) => string
  /** Regex to detect this mark in markdown. Must capture the inner text. */
  pattern: RegExp
  /** Construct the AST Mark value from a regex match */
  toMark: (match: RegExpMatchArray) => Mark
  /** Which capture group has the inner text (default: 1) */
  textGroup?: number
}

export const MARK_FORMATS: MarkFormatEntry[] = [
  // Bold: **text**
  {
    matchMark: (m) => m === 'bold',
    render: (text) => `**${text}**`,
    pattern: /(?<!\\)\*\*([^*]+)\*\*/,
    toMark: () => 'bold',
  },
  // Italic: *text*
  {
    matchMark: (m) => m === 'italic',
    render: (text) => `*${text}*`,
    pattern: /(?<!\*)(?<!\\)\*(?!\*)([^*]+)\*(?!\*)/,
    toMark: () => 'italic',
  },
  // Underline: <u>text</u>
  {
    matchMark: (m) => m === 'underline',
    render: (text) => `<u>${text}</u>`,
    pattern: /<u>([^<]+)<\/u>/,
    toMark: () => 'underline',
  },
  // Font family: <span style="font-family: ...">text</span>
  {
    matchMark: (m) => typeof m === 'object' && m.type === 'font',
    render: (text, mark) => {
      const m = mark as { type: 'font'; family: string }
      return `<span style="font-family: ${m.family}">${text}</span>`
    },
    pattern: /<span style="font-family: ([^"]+)">([^<]+)<\/span>/,
    toMark: (match) => ({
      type: 'font',
      family: match[1] as 'Arial' | 'Times' | 'Helvetica' | 'Georgia' | 'Verdana',
    }),
    textGroup: 2,
  },
  // Color: <span style="color: ...">text</span>
  {
    matchMark: (m) => typeof m === 'object' && m.type === 'color',
    render: (text, mark) => {
      const m = mark as { type: 'color'; value: string }
      return `<span style="color: ${m.value}">${text}</span>`
    },
    pattern: /<span style="color: ([^"]+)">([^<]+)<\/span>/,
    toMark: (match) => ({ type: 'color', value: match[1] }),
    textGroup: 2,
  },
]
